import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth, API } from "@/App";
import { useLanguage } from "@/contexts/LanguageContext";
import { 
  ArrowLeft, Stethoscope, Building2, MessageCircle, 
  Phone, Mail, Calendar, Clock, FileText, Star
} from "lucide-react";

const CareTeamDetails = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { t } = useLanguage();
  const [careTeam, setCareTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mealPlans, setMealPlans] = useState([]);

  useEffect(() => {
    fetchCareTeamDetails();
    fetchAssignedMealPlans();
  }, [token]);

  const fetchCareTeamDetails = async () => {
    try {
      const response = await fetch(`${API}/care-team/my-team`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCareTeam(data);
      }
    } catch (error) {
      console.error("Failed to fetch care team:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignedMealPlans = async () => {
    try {
      const response = await fetch(`${API}/meal-plans/assigned`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMealPlans(data.meal_plans || []);
      }
    } catch (error) {
      console.error("Failed to fetch meal plans:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white p-4">
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!careTeam?.has_care_team) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white p-4">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('common.back')}
        </Button>
        <Card className="p-8 text-center">
          <Stethoscope className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">{t('care_team.no_care_team')}</h2>
          <p className="text-muted-foreground mb-4">
            {t('care_team.no_care_team_desc')}
          </p>
          <Button onClick={() => navigate("/find-dietitian")}>
            {t('care_team.find_dietitian')}
          </Button>
        </Card>
      </div>
    );
  }

  const { care_team, partner_name, assigned_at } = careTeam;

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-400 text-white p-4 pb-20">
        <div className="flex items-center gap-3 mb-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="text-white hover:bg-white/20"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-xl font-bold">{t('care_team.title')}</h1>
        </div>
      </div>

      {/* Content - overlapping header */}
      <div className="px-4 -mt-16 pb-6 space-y-4">
        {/* Dietitian Card */}
        <Card className="p-6" data-testid="dietitian-details-card">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
              {care_team.picture ? (
                <img 
                  src={care_team.picture} 
                  alt={care_team.name}
                  className="w-16 h-16 rounded-full object-cover"
                />
              ) : (
                <Stethoscope className="w-8 h-8 text-orange-600" />
              )}
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-900">{care_team.name}</h2>
              <p className="text-orange-600 font-medium">{t('care_team.your_dietitian')}</p>
              {care_team.specialization && (
                <p className="text-sm text-gray-500 mt-1">{care_team.specialization}</p>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 mt-6">
            <Button 
              className="flex-1 bg-orange-500 hover:bg-orange-600"
              onClick={() => navigate(`/messages/${care_team.dietitian_id}`)}
              data-testid="message-dietitian-btn"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              {t('care_team.message')}
            </Button>
            {care_team.phone && (
              <Button 
                variant="outline"
                className="flex-1"
                onClick={() => window.location.href = `tel:${care_team.phone}`}
              >
                <Phone className="w-4 h-4 mr-2" />
                {t('care_team.call')}
              </Button>
            )}
          </div>

          {/* Contact Info */}
          {care_team.email && (
            <div className="flex items-center gap-3 mt-4 pt-4 border-t">
              <Mail className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-600">{care_team.email}</span>
            </div>
          )}

          {/* Member Since */}
          {assigned_at && (
            <div className="flex items-center gap-3 mt-3">
              <Calendar className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-600">
                {t('care_team.patient_since', { date: new Date(assigned_at).toLocaleDateString(undefined, { 
                  month: 'long', 
                  year: 'numeric' 
                }) })}
              </span>
            </div>
          )}
        </Card>

        {/* Organization Card */}
        {partner_name && (
          <Card className="p-6" data-testid="organization-card">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center">
                <Building2 className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{partner_name}</h3>
                <p className="text-sm text-gray-500">{t('care_team.partner_organization')}</p>
              </div>
            </div>
          </Card>
        )}

        {/* Assigned Meal Plans */}
        <Card className="p-6" data-testid="assigned-plans-card">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-orange-500" />
            {t('care_team.assigned_meal_plans')}
          </h3>
          
          {mealPlans.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">
              {t('care_team.no_meal_plans_assigned')}
            </p>
          ) : (
            <div className="space-y-3">
              {mealPlans.map((plan) => (
                <div 
                  key={plan.plan_id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
                  onClick={() => navigate(`/meal-plan/${plan.plan_id}`)}
                >
                  <div>
                    <p className="font-medium text-gray-900">{plan.name}</p>
                    <p className="text-xs text-gray-500">
                      {plan.total_calories} kcal/day
                    </p>
                  </div>
                  <Button variant="ghost" size="sm">
                    {t('care_team.view')}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Quick Stats */}
        <Card className="p-6">
          <h3 className="font-semibold text-gray-900 mb-4">{t('care_team.your_progress')}</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-3 bg-green-50 rounded-xl">
              <p className="text-2xl font-bold text-green-600">
                {user?.compliance_score || 0}%
              </p>
              <p className="text-xs text-gray-500">{t('care_team.compliance_score')}</p>
            </div>
            <div className="text-center p-3 bg-blue-50 rounded-xl">
              <p className="text-2xl font-bold text-blue-600">
                {user?.streak_days || 0}
              </p>
              <p className="text-xs text-gray-500">{t('care_team.day_streak')}</p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default CareTeamDetails;
